import { t, Trans } from '@lingui/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { tracking, useAuthContext } from 'noddi-provider';
import { BookingProposalItem, SelectedSalesItemsCarGrouped } from 'noddi-types';
import { NoddiButton } from 'noddi-ui-common';
import { ContentWrapper, NoddiBasicCard } from 'noddi-ui-web';
import { customerRoutes, getCarDisplayName } from 'noddi-util';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiErrorMessageWithTrans } from '../../../components/ApiErrorMessageWithTrans';
import { BookingProposalNextSteps } from '../../../components/BookingProposal/BookingProposalNextSteps';
import { TireQuoteAttachedToItem } from '../../../components/BookingProposal/TireQuoteAttachedToItem';
import { CustomerSMSLogin } from '../../../components/CustomerSMSLogin';
import { NoddiCarSvg } from '../../../components/Home/CarSvg';
import { TransparentBottomBar } from '../../../components/Layouts/TransparentBottomBar';
import { CommonBookingInfoCard } from '../../../components/Summary/CommonBookingInfoCard';
import CommentBox from '../../BookingFlow/Steps/Summary/CommentBox';
import {
  useBookingProposalAddress,
  useBookingProposalItems,
  useBookingProposalTimeWindow
} from '../bookingProposalStore';

export const BookingProposalConfirmation = () => {
  const { isLoggedIn } = useAuthContext();
  const [commentsUser, setCommentsUser] = useState('');
  const address = useBookingProposalAddress();
  const selectedBookingTimeWindow = useBookingProposalTimeWindow();
  const bookingProposalItems = useBookingProposalItems();
  const { currentUserGroupId: userGroupId, userData } = useAuthContext();
  const navigate = useNavigate();

  const {
    mutateAsync: mutateAsyncCreateBooking,
    isPending,
    error: createBookingError
  } = noddiAsync.usePost({
    type: URLKeys.postCreateBooking,
    queryConfig: {
      onSuccess: ({ data }) => {
        tracking.track('bookingProposalConfirmation');
        navigate(customerRoutes.myBookingDetails.getPath({ id: data.id }));
      }
    }
  });

  const mapBookingProposalItemsToSelectedSalesItemsCars = (
    bookingProposalItems: BookingProposalItem[]
  ): SelectedSalesItemsCarGrouped[] => {
    return bookingProposalItems.map((bookingProposalItem) => ({
      licensePlate: bookingProposalItem.car.licensePlate,
      selectedSalesItemIds: bookingProposalItem.salesItems.map((salesItem) => salesItem.item.id)
    }));
  };

  const onSubmit = async () => {
    await mutateAsyncCreateBooking({
      cars: mapBookingProposalItemsToSelectedSalesItemsCars(bookingProposalItems),
      addressId: address!.id,
      deliveryWindow: {
        id: selectedBookingTimeWindow!.deliveryWindowId,
        endsAt: selectedBookingTimeWindow!.endsAt,
        startsAt: selectedBookingTimeWindow!.startsAt
      },
      userId: userData!.user.id,
      userGroupId: userGroupId!,
      commentsUser,
      notificationsToOverride: []
    });
  };

  return (
    <ContentWrapper title={t`Does this look correct?`} className='max-w-screen-md'>
      <div className='mb-22 flex flex-col gap-4 md:mb-0'>
        <NoddiBasicCard>
          {bookingProposalItems.map((bookingProposalItem) => (
            <div key={bookingProposalItem.id} className='flex flex-col gap-2'>
              <div className='flex items-center justify-between gap-4'>
                <h1 className='font-semibold'>{getCarDisplayName(bookingProposalItem.car)}</h1>
                <NoddiCarSvg scale={0.5} />
              </div>

              <div className='flex flex-col border-t pt-2'>
                {bookingProposalItem.tireQuote && <TireQuoteAttachedToItem slug={bookingProposalItem.tireQuote.slug} />}
                {bookingProposalItem.salesItems.map(({ item: { name } }) => (
                  <p key={name}>{name}</p>
                ))}
              </div>
            </div>
          ))}
        </NoddiBasicCard>
        <CommonBookingInfoCard
          address={address}
          timeWindow={selectedBookingTimeWindow}
          excludeSteps={['communication']}
        />

        {isLoggedIn ? (
          <>
            <BookingProposalNextSteps />
            <CommentBox
              comment={commentsUser}
              handleChange={setCommentsUser}
              title={t`Anything more we should know?`}
              placeholder={t`Optional note if any extra information is needed...`}
            />
          </>
        ) : (
          <NoddiBasicCard>
            <div className='text-darkPurple'>
              <p className='mb-2 font-bold'>
                <Trans>You need to log in in order to confirm the order</Trans>
              </p>
              <CustomerSMSLogin />
            </div>
          </NoddiBasicCard>
        )}
      </div>
      <TransparentBottomBar>
        {createBookingError && (
          <div className='mb-4'>
            <ApiErrorMessageWithTrans error={createBookingError} />
          </div>
        )}
        <div className='md:flex md:justify-end'>
          <NoddiButton
            loading={isPending}
            disabled={!isLoggedIn || isPending}
            variant='success'
            className='w-full md:w-fit'
            onPress={onSubmit}
          >
            <Trans>Confirm booking</Trans>
          </NoddiButton>
        </div>
      </TransparentBottomBar>
    </ContentWrapper>
  );
};
